import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { usePagePermission } from '@/hooks/usePagePermission'
import {
  useGetPurchaseRepInfoQuery,
  useSetPurchaseRepInfoMutation,
} from '@/store/api/purchaseContractApi'

import { compactDialogContentSx, dialogTitleSx } from './dialogStyle'

interface PurchaseChooseRepInfoDialogProps {
  open: boolean
  onClose: () => void
}

export default function PurchaseChooseRepInfoDialog({
  open,
  onClose,
}: PurchaseChooseRepInfoDialogProps) {
  const { canEdit } = usePagePermission()
  const { data, isLoading } = useGetPurchaseRepInfoQuery(undefined, { skip: !open })
  const [setPurchaseRepInfo, { isLoading: isSaving }] = useSetPurchaseRepInfoMutation()
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setSelectedId(null)
      setSaveError(null)
    }
  }, [open])

  const currentId = selectedId ?? data?.currentSignId ?? null
  const rows = data?.items ?? []

  const handleSave = async () => {
    if (currentId == null) return
    setSaveError(null)
    try {
      await setPurchaseRepInfo(currentId).unwrap()
      onClose()
    } catch {
      setSaveError('Failed to save representative info.')
    }
  }

  const handleCancel = () => {
    setSelectedId(null)
    setSaveError(null)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" sx={{ flex: 1, fontWeight: 700, fontSize: '1rem' }}>
          Choose Representative Info
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Button
            variant="contained"
            size="small"
            color="success"
            disabled={currentId == null || isSaving || !canEdit}
            onClick={handleSave}
          >
            {isSaving ? <CircularProgress size={14} color="inherit" /> : 'OK'}
          </Button>
          <Button variant="outlined" size="small" onClick={handleCancel}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={{ ...compactDialogContentSx, p: 0 }}>
        {saveError ? <Alert severity="error" sx={{ m: 1.5 }}>{saveError}</Alert> : null}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No representative records found in <code>public.sign_info</code>.
          </Typography>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Position</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  selected={currentId === row.id}
                  onClick={() => setSelectedId(row.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox">
                    <Radio
                      size="small"
                      checked={currentId === row.id}
                      onChange={() => setSelectedId(row.id)}
                      onClick={(event) => event.stopPropagation()}
                    />
                  </TableCell>
                  <TableCell>{row.name ?? '—'}</TableCell>
                  <TableCell>{row.position ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  )
}
